import * as React from 'react';
import { DataGrid, GridApi, GridColDef } from '@mui/x-data-grid'; // Use GridValueFormatter
import { Button } from '@mui/material';
import DataGridViewport from './DataGridViewport';

type HeaderMapper = {
  [key: string]: { Header: string, Editable?: boolean | false, IsNumber?: boolean }
};

type NestedObject = {
  [key: string]: any;
};

function flattenObject(obj: any, prefix = "", result: any = {}) {
  for (const key in obj) {
    if (typeof obj[key] === "object" && obj[key] !== null) {
      // Avoid circular references
      if (obj[key] === obj) continue;
      flattenObject(obj[key], `${prefix}${key}.`, result);
    } else {
      result[`${prefix}${key}`] = obj[key];
    }
  }
  return result;
}

const getColumnsFromObject = (
  data: NestedObject[],
  headerMap: HeaderMapper,
  removeIfNotCustom: boolean,
  actionColumn?: {
    actionColumnName?: string;
    withActionColumn?: boolean;
    eventHandler?: any;
  },
  customRenderers?: {
    [key: string]: (row: any, rowIndex: number) => JSX.Element | string;
  }
): GridColDef[] => {
  const sampleData = data[0] ? flattenObject(data[0]) : {};
  const gridColDef: GridColDef[] = Object.keys(sampleData)
    .filter((key) => {
      return removeIfNotCustom ? headerMap[key.toUpperCase()]?.Header : key;
    })
    .map((key) => ({
      field: key,
      headerName: headerMap[key.toUpperCase()]?.Header || key.replace('.', ' '),
      type: headerMap[key.toUpperCase()]?.IsNumber ? "number" : undefined,
      align: headerMap[key.toUpperCase()]?.IsNumber ? "right" : "left",
      minWidth: 150,
      flex: 1,
      editable: headerMap[key.toUpperCase()]?.Editable ?? false,
      renderCell: customRenderers && customRenderers[key]
        ? (params) => customRenderers[key](params.row, params.id as number) // Apply custom renderer
        : undefined,
    }));

  if (actionColumn?.withActionColumn) {
    const actionColDef: GridColDef = {
      field: "action",
      headerName: "Action",
      sortable: false,
      renderCell: (params) => {
        const onClick = (e: any) => {
          const api: GridApi = params.api;
          const thisRow: Record<string, any> = {};

          api
            .getAllColumns()
            .filter((c) => c.field !== "__check__" && !!c)
            .forEach(
              (c) => (thisRow[c.field] = params.value(params.id, c.field))
            );
          const _row = JSON.stringify(thisRow);
          if (actionColumn.eventHandler) {
            return actionColumn.eventHandler(_row);
          } else {
            return alert(_row);
          }
        };

        return (
          <Button onClick={onClick}>
            {actionColumn?.actionColumnName ?? "Click"}
          </Button>
        );
      },
    };
    gridColDef.push(actionColDef);
  }
  return gridColDef;
};

type Props = {
  data: NestedObject[];
  customHeaderMap: HeaderMapper;
  removeIfNotCustom: boolean;
  pageSize: number;
  actionColumn?: {
    actionColumnName?: string;
    withActionColumn?: boolean;
    eventHandler?: any;
  };
  rowClickEventHandler?: any;
  customRenderers?: {
    [key: string]: (row: any, rowIndex: number) => JSX.Element;
  };
};

const DynamicGrid: React.FC<Props> = ({
  data,
  customHeaderMap,
  removeIfNotCustom,
  pageSize: initialPageSize,
  actionColumn,
  rowClickEventHandler,
  customRenderers,
}) => {
  const [page, setPage] = React.useState(0); // State to manage the current page
  const [pageSize, setPageSize] = React.useState(initialPageSize); // State to manage the current page size

  const rows = data.map((item, index) => ({
    id: index,
    ...flattenObject(item),
  }));
  const columns: GridColDef[] = getColumnsFromObject(
    data,
    customHeaderMap,
    removeIfNotCustom,
    actionColumn,
    customRenderers // Pass customRenderers to getColumnsFromObject
  );

  const headerHeight = 56; // Typical header height in pixels
  const rowHeight = 52; // Typical row height in pixels
  const footerHeight = 56; // Typical footer height in pixels
  const gridHeight =
    rows.length > 0
      ? Math.min(rows.length, pageSize) * rowHeight + headerHeight + footerHeight
      : footerHeight;

  return (
    <DataGridViewport height={gridHeight} minWidth={900}>
      <DataGrid
        rows={rows}
        columns={columns}
        autoPageSize={false}
        pageSizeOptions={[5, 10, 20]}
        paginationModel={{ page, pageSize }}
        onPaginationModelChange={(model) => {
          setPage(model.page); // Update page state
          setPageSize(model.pageSize); // Update pageSize state
        }}
        sortingMode="client"
        filterMode="client"
        onRowClick={rowClickEventHandler}
      />
    </DataGridViewport>
  );
};

export default DynamicGrid;
